import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const SPINY_SPHERE_IMG =
  "https://customer-assets.emergentagent.com/job_bigibila-react/artifacts/wgfjrskc_1000019640.jpg";

// Framer Motion variants (hoisted so new object references aren't allocated per render).
const CARD_MOTION = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: "easeOut" },
};
const EMPTY_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

// Hotspot positions are calibrated as percentages of the square diagram.
// Each maps to one of the diagram's external spines (right-side buttons).
const SPINES = [
  {
    id: "fpic",
    label: "FPIC",
    title: "Free, Prior and Informed Consent",
    cssTop: "35%",
    cssLeft: "87%",
    body:
      "Communities must be asked before anything happens, given enough information to actually understand it, and able to say no — or change their mind later. Consent is time-bound, scoped and revocable.",
    examples: [
      "A research team requests use of community language records.",
      "A government program asks to publish a community story.",
      "A media organisation wants to film on Country.",
    ],
  },
  {
    id: "icip",
    label: "ICIP",
    title: "Indigenous Collective Intellectual Property",
    cssTop: "48%",
    cssLeft: "89%",
    body:
      "Stories, language, songs, designs, knowledge and the cultural rights attached to them belong to the community of origin — not to individuals, agencies or contractors. Bigibila records the asset, the authority and the conditions of use.",
    examples: [
      "Asset register with named authority for each item.",
      "Approved-use statements and use restrictions.",
      "Takedown pathways when conditions are breached.",
    ],
  },
  {
    id: "ids",
    label: "IDS",
    title: "Indigenous Data Sovereignty",
    cssTop: "60%",
    cssLeft: "88%",
    body:
      "Communities have the right to govern data about their people, Country and culture — including where it is stored, who can access it, how it is interpreted, and when it must be returned or destroyed.",
    examples: [
      "Data custodianship and provenance recorded for each dataset.",
      "Sovereign hosting and controlled access tiers.",
      "Sovereignty risk indicators reviewed at every release.",
    ],
  },
  {
    id: "escalation",
    label: "Escalation",
    title: "Dispute Resolution & Escalation Pathways",
    cssTop: "71%",
    cssLeft: "85%",
    body:
      "When authority, consent or cultural protection is unresolved, the issue is escalated through Q-Level pathways. Q2 is moderate (e.g. unclear authority). Q3 is urgent (e.g. potential ICIP misuse).",
    examples: [
      "Q2: No confirmed approval body — governance lead has 14 days to resolve.",
      "Q3: Use beyond approved purpose — executive sponsor has 48 hours.",
    ],
  },
  {
    id: "access",
    label: "Access Control",
    title: "Enforced Access, Audit & Monitoring",
    cssTop: "81%",
    cssLeft: "79%",
    body:
      "Every interaction with protected material is gated by authority, purpose and recorded consent — and every decision is captured as cryptographic audit evidence ready for boards, councils and auditors.",
    examples: [
      "Role-based access tiers tied to recorded authority.",
      "Immutable audit trail of every decision.",
      "Exportable evidence packs for assurance reviews.",
    ],
  },
];

export default function SpinySphereExplorer() {
  const [activeId, setActiveId] = useState(null);
  const active = SPINES.find((s) => s.id === activeId);

  const handleSelect = (id) => {
    const next = activeId === id ? null : id;
    setActiveId(next);
    if (next) trackEvent("spiny_spine_clicked", { spine: next });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      {/* Diagram + hotspots */}
      <div className="lg:col-span-7">
        <figure className="relative" data-testid="architecture-spiny-sphere">
          <div className="relative overflow-hidden bg-stone-950 border border-stone-900 shadow-2xl">
            <img
              src={SPINY_SPHERE_IMG}
              alt="BIGIBILA Spiny Sphere™ — Indigenous Data Governance Architecture. Inner rings: Lore, Governance, Community, Permissions, Metadata, Systems. External spines: FPIC, ICIP, IDS, Escalation, Access Control."
              className="w-full h-auto block"
              loading="lazy"
            />

            {/* Hotspots */}
            {SPINES.map((s) => {
              const isActive = activeId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleSelect(s.id)}
                  data-testid={`spiny-hotspot-${s.id}`}
                  aria-label={`${s.label} — ${s.title}`}
                  aria-pressed={isActive}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-300 focus:outline-none"
                  style={{
                    top: s.cssTop,
                    left: s.cssLeft,
                    width: "11%",
                    height: "11%",
                  }}
                >
                  <span
                    className={`absolute inset-0 rounded-full border-2 ${
                      isActive
                        ? "border-[#E4B58E] shadow-[0_0_30px_8px_rgba(228,181,142,0.55)]"
                        : "border-transparent group-hover:border-[#E4B58E]/40"
                    } transition-all duration-300`}
                  />
                  {/* outer pulse ring on inactive — subtle invitation to interact */}
                  {!isActive && (
                    <span className="absolute inset-[-12%] rounded-full border border-[#E4B58E]/40 animate-pulse" />
                  )}
                </button>
              );
            })}
          </div>
          <figcaption className="mt-4 text-center text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">
            Tap a spine to explore · Strong Foundations · Living Governance · Sovereign Futures
          </figcaption>
        </figure>

        {/* Mobile quick-pick row — also acts as desktop legend */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="spiny-spine-picker">
          {SPINES.map((s) => {
            const isActive = activeId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => handleSelect(s.id)}
                data-testid={`spiny-pill-${s.id}`}
                className={`text-[10px] tracking-[0.18em] uppercase font-bold px-3 py-2 border transition-colors ${
                  isActive
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-stone-300 text-stone-700 hover:border-stone-900"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Explainer card */}
      <div className="lg:col-span-5">
        <AnimatePresence mode="wait">
          {active ? (
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-7 relative"
              data-testid={`spiny-card-${active.id}`}
            >
              <button
                type="button"
                onClick={() => setActiveId(null)}
                aria-label="Close explainer"
                data-testid="spiny-card-close"
                className="absolute top-4 right-4 text-stone-500 hover:text-stone-900"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221]">
                External spine · {active.label}
              </div>
              <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight font-bold text-stone-900 leading-snug">
                {active.title}
              </h3>
              <p className="mt-4 text-stone-800 leading-relaxed">{active.body}</p>
              <div className="mt-6">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-stone-600 mb-3">
                  What this looks like in practice
                </div>
                <ul className="space-y-2.5">
                  {active.examples.map((ex) => (
                    <li
                      key={ex}
                      className="flex gap-3 text-sm text-stone-700 leading-relaxed"
                    >
                      <span className="mt-2 h-1 w-1 shrink-0 bg-[#9C4221] rounded-full" />
                      {ex}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              {...EMPTY_MOTION}
              className="border border-dashed border-stone-300 bg-stone-50 p-7"
              data-testid="spiny-card-empty"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">
                External spines
              </div>
              <h3 className="mt-3 font-display text-xl md:text-2xl tracking-tight font-bold text-stone-900 leading-snug">
                Tap any spine on the diagram to see what it does in plain English.
              </h3>
              <p className="mt-4 text-sm text-stone-600 leading-relaxed">
                The five external spines — FPIC, ICIP, IDS, Escalation and Access Control — are the protective extensions that connect, enforce and uphold the sphere. They are the operational controls that turn the architecture into working governance.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
